import React, { useState, useEffect } from 'react'
import TableContainer, {
  TableContainerProps,
} from '@mui/material/TableContainer'
import {
  Box,
  Table as MuiTable,
  TableProps as MuiTableProps,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import palette from '../Themes/palette'
import TableRow from '@mui/material/TableRow'
import TablePagination, {
  TablePaginationProps,
} from '@mui/material/TablePagination'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import TableHeader from './TableHeader'
import { TableHeadProps } from '@mui/material/TableHead'
import TableBody, { TableBodyProps } from '@mui/material/TableBody'
import LinearProgress from '@mui/material/LinearProgress'
import TableCell from '@mui/material/TableCell'
import Text from '../Text'

const Container = styled('div')({
  borderRadius: '0.525rem',
  border: `1px solid ${palette.grey.grey300}`,
  overflow: 'hidden',
})

const TableMainContainer = styled(TableContainer)({
  scrollbarWidth: 'thin',
  scrollbarColor: `${palette.grey.grey300} transparent`,
  '& .MuiTableRow-root:last-child td': {
    borderBottom: 'none',
  },
})

const TablePaginationContainer = styled(TablePagination)({
  borderTop: `1px solid ${palette.grey.grey300}`,
  borderBottom: 'none',
  scrollbarWidth: 'thin',
  scrollbarColor: `${palette.grey.grey300} transparent`,
})

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void
}

const TablePaginationActions = (props: TablePaginationActionsProps) => {
  const { count, page, rowsPerPage, onPageChange } = props

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1)
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0, padding: '0.75rem 0' }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label='previous page'
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  )
}

export interface TableProps extends MuiTableProps {
  maxRows?: number
  headerCells?: React.ReactNode[]
  rows?: React.ReactNode[]
  isLoading?: boolean
  showPagination?: boolean
  showRows?: boolean
  tableContainerProps?: TableContainerProps
  tableHeaderProps?: TableHeadProps
  tableBodyProps?: TableBodyProps
  tablePaginationProps?: TablePaginationProps
  emptyMessage?: string
  stickyColumns?: number
  resetPaginationOnRowsChange?: boolean
}

const Table = (props: TableProps) => {
  const {
    rows = [],
    maxRows = 5,
    headerCells,
    isLoading = false,
    showPagination = true,
    showRows = true,
    tableContainerProps,
    tableHeaderProps,
    tableBodyProps,
    tablePaginationProps,
    emptyMessage,
    stickyColumns = 0,
    resetPaginationOnRowsChange = true,
    ...rest
  } = props

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(maxRows)
  const [shouldShowPagination, setShouldShowPagination] =
    useState(showPagination)
  const [isLoadingHere, setIsLoadingHere] = useState(isLoading)
  const [isScrolled, setIsScrolled] = useState(false)
  const tableContainerRef = React.useRef<HTMLDivElement>(null)

  // Check if the table is scrolled
  useEffect(() => {
    const tableContainer = tableContainerRef.current
    if (!tableContainer) return

    const handleScroll = () => {
      setIsScrolled(tableContainer.scrollLeft > 0)
    }

    tableContainer.addEventListener('scroll', handleScroll)
    return () => {
      tableContainer.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Applying sticky columns css
  useEffect(() => {
    const table = document.getElementById('scrollable-table')
    if (!table) return

    const ths = table.querySelectorAll('thead th')
    const rows = table.querySelectorAll('tbody tr')

    // Inject CSS class once
    if (!document.getElementById('sticky-column-style')) {
      const styleSheet = document.createElement('style')
      styleSheet.id = 'sticky-column-style'
      styleSheet.type = 'text/css'
      styleSheet.innerText = `
        .sticky-column {
          position: sticky;
          z-index: 1;
          background-color: ${palette.secondary.purple100};
          transition: left 0.8s ease-in-out;
        }
  
        .sticky-column-last::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 1px;
          background: ${palette.grey.grey300};
          box-shadow: 2px 0 8px rgba(0, 0, 0, 0.4);
          transition: left 0.8s ease-in-out;
        }
      `
      document.head.appendChild(styleSheet)
    }

    // Reset previous sticky styles
    ths.forEach((th) => {
      th.classList.remove('sticky-column', 'sticky-column-last')
      th.style.left = ''
    })
    rows.forEach((tr) => {
      Array.from(tr.children).forEach((td) => {
        td.classList.remove('sticky-column', 'sticky-column-last')
        ;(td as HTMLElement).style.left = ''
      })
    })

    if (!isScrolled) return

    // Apply sticky column styles
    let thLeft = 0
    for (let i = 0; i < stickyColumns; i++) {
      const th = ths[i] as HTMLElement
      if (!th) continue

      th.classList.add('sticky-column')
      if (i === stickyColumns - 1) th.classList.add('sticky-column-last')

      th.style.left = `${thLeft}px`
      thLeft += th.offsetWidth
    }

    rows.forEach((tr) => {
      let tdLeft = 0
      for (let i = 0; i < stickyColumns; i++) {
        const td = tr.children[i] as HTMLElement
        if (!td) continue

        td.classList.add('sticky-column')
        if (i === stickyColumns - 1) td.classList.add('sticky-column-last')

        td.style.left = `${tdLeft}px`
        tdLeft += td.offsetWidth
      }
    })
  }, [stickyColumns, isScrolled, page])

  useEffect(() => {
    setIsLoadingHere(isLoading)
  }, [isLoading])

  // Row to display
  const rowsToDisplay = rows?.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  )

  // Reset the pagination when the rows change
  useEffect(() => {
    if (resetPaginationOnRowsChange) {
      setPage(0)
    }
  }, [rows, resetPaginationOnRowsChange])

  useEffect(() => {
    if (rows?.length <= maxRows) {
      setShouldShowPagination(false)
    } else {
      setShouldShowPagination(true)
    }
  }, [rows, maxRows])

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setIsLoadingHere(true)
    // Wait for 500ms before setting the page
    // for visual effect
    setTimeout(() => {
      setPage(newPage)
      setIsLoadingHere(false)
    }, 500)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value))
    setPage(0)
  }

  return (
    <Container>
      <TableMainContainer {...tableContainerProps} ref={tableContainerRef}>
        <MuiTable {...rest} id='scrollable-table'>
          {headerCells && (
            <TableHeader {...tableHeaderProps}>
              {headerCells.map((cell: React.ReactNode) => cell)}
            </TableHeader>
          )}
          {isLoadingHere && (
            <TableRow>
              <td colSpan={headerCells?.length || 1} style={{ padding: 0 }}>
                <LinearProgress
                  sx={{
                    backgroundColor: palette.secondary.purple300,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: palette.secondary.purple600,
                    },
                    width: '100%',
                  }}
                />
              </td>
            </TableRow>
          )}
          {showRows && (
            <TableBody {...tableBodyProps}>
              {rows?.length > 0 ? (
                rowsToDisplay?.map((row: React.ReactNode) => row)
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={headerCells?.length || 1}
                    style={{
                      padding: '1.5rem',
                    }}
                  >
                    <Text variant='action4'>
                      {emptyMessage || 'No Results Found'}
                    </Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </MuiTable>
      </TableMainContainer>
      {shouldShowPagination && (
        <TablePaginationContainer
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          count={rows?.length || 0}
          component='div'
          rowsPerPage={rowsPerPage}
          page={page}
          slotProps={{
            select: {
              inputProps: {
                'aria-label': 'Show rows per page',
                style: {
                  fontFamily: 'polySans',
                },
              },
            },
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          {...tablePaginationProps}
        />
      )}
    </Container>
  )
}

export default React.memo(Table)
